import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { http, fmtErr, WS_BASE } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "../components/ui/drawer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { fmtTRY, fmtGold, fmtDate } from "../lib/format";
import {
  Gift, Send, Users, Coins, Sparkles, Radio,
  Ticket, MessageCircleHeart, HandHeart, Banknote, GemIcon,
} from "lucide-react";

const KIND_META = {
  cash:     { icon: Banknote,           label_key: "cash" },
  gold:     { icon: GemIcon,            label_key: "gold" },
  coupon:   { icon: Ticket,             label_key: "coupon" },
  message:  { icon: MessageCircleHeart, label_key: "message_gift" },
  donation: { icon: HandHeart,          label_key: "donation" },
};

function StreamEmbed({ url }) {
  const src = useMemo(() => {
    if (!url) return null;
    try {
      const u = new URL(url);
      if (u.hostname.includes("youtube.com") || u.hostname.includes("youtu.be")) {
        let id = u.searchParams.get("v");
        if (!id && u.hostname === "youtu.be") id = u.pathname.replace("/", "");
        if (!id && u.pathname.startsWith("/embed/")) id = u.pathname.split("/embed/")[1];
        if (id) return `https://www.youtube.com/embed/${id}`;
      }
      return url;
    } catch { return null; }
  }, [url]);
  if (!src) {
    return (
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 ring-1 ring-primary/30 grid place-items-center mb-4 pulse-gold">
            <Radio className="w-7 h-7 text-primary" />
          </div>
          <div className="font-serif text-2xl">Yayın Başlamayı Bekliyor</div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">Waiting Room</div>
        </div>
      </div>
    );
  }
  return <iframe title="stream" src={src} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" />;
}

function CountdownRing({ seconds, total = 90 }) {
  const pct = Math.max(0, Math.min(1, seconds / total));
  const r = 32; const c = 2 * Math.PI * r;
  return (
    <div className="relative w-20 h-20">
      <svg viewBox="0 0 72 72" className="w-full h-full -rotate-90">
        <circle cx="36" cy="36" r={r} strokeWidth={4} className="stroke-muted fill-none" />
        <circle cx="36" cy="36" r={r} strokeWidth={4} className="stroke-primary fill-none transition-all duration-700"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="font-mono text-xl">{seconds}</div>
      </div>
    </div>
  );
}

function GiftCard({ g, i, t }) {
  const meta = KIND_META[g.kind] || KIND_META.cash;
  const Icon = meta.icon;
  const label = t[meta.label_key] || g.kind;
  const isGold = g.kind === "gold";
  const isCoupon = g.kind === "coupon";
  const isMessage = g.kind === "message";
  return (
    <div className="p-3 rounded-lg bg-muted/30 flex items-start justify-between gap-3 animate-float-up"
         style={{ animationDelay: `${i * 30}ms` }} data-testid={`live-wall-${i}`}>
      <div className="flex items-start gap-3 min-w-0">
        <span className={`w-9 h-9 rounded-full grid place-items-center ring-1 ring-primary/25 shrink-0 ${isGold ? "bg-primary/15 text-primary" : isCoupon ? "bg-amber-500/10 text-amber-400" : isMessage ? "bg-rose-500/10 text-rose-300" : g.kind === "donation" ? "bg-emerald-500/10 text-emerald-300" : "bg-primary/10 text-primary"}`}>
          <Icon className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <div className="font-serif truncate">{g.display_name}</div>
          {isMessage ? (
            <div className="text-sm text-foreground/90 mt-0.5 line-clamp-2">&ldquo;{g.text}&rdquo;</div>
          ) : g.message ? (
            <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">&ldquo;{g.message}&rdquo;</div>
          ) : null}
          {isCoupon && <div className="text-[10px] uppercase tracking-wider text-amber-400 mt-1">{t.coupon_from}: {g.partner}</div>}
          {g.kind === "donation" && <div className="text-[10px] uppercase tracking-wider text-emerald-300 mt-1">{t.donation_to}: {g.cause_name}</div>}
        </div>
      </div>
      <div className="text-end shrink-0">
        <div className="text-[10px] uppercase tracking-wider text-primary/70">{label}</div>
        <div className="font-mono text-sm">
          {isGold ? fmtGold(g.grams_mg) : isMessage ? "—" : fmtTRY(g.amount_kurus)}
        </div>
      </div>
    </div>
  );
}

function GiftFlow({ event, guestToken, displayName, catalog, onDone }) {
  const { t } = useI18n();
  const [kind, setKind] = useState(event.accept_cash ? "cash" : (event.accept_gold ? "gold" : "coupon"));
  const [amountTL, setAmountTL] = useState(500);
  const [grams, setGrams] = useState(1000);
  const [couponId, setCouponId] = useState(catalog.coupons[0]?.coupon_id || "");
  const [causeId, setCauseId]   = useState(catalog.causes[0]?.cause_id || "");
  const [text, setText] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [message, setMessage] = useState("");
  const [lock, setLock] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [gift, setGift] = useState(null);
  const [phase, setPhase] = useState("form"); // form|lock|paying|done
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (phase !== "lock" || !lock) return;
    const tick = () => {
      const remain = Math.max(0, Math.floor((new Date(lock.expires_at).getTime() - Date.now()) / 1000));
      setSeconds(remain);
      if (remain <= 0) { setPhase("form"); setLock(null); toast.warning("Fiyat kilidi süresi doldu."); }
    };
    tick();
    const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
  }, [phase, lock]);

  const chosenCoupon = catalog.coupons.find(c => c.coupon_id === couponId);
  const chosenCause  = catalog.causes.find(c => c.cause_id === causeId);
  const authHeader = { headers: { Authorization: `Bearer ${guestToken}` } };

  const initGift = async (extraPayload) => {
    const payload = {
      event_id: event.id,
      guest_token: guestToken,
      display_name: displayName,
      anonymous,
      message,
      ...extraPayload,
    };
    const { data } = await http.post("/gifts/init", payload, authHeader);
    return data;
  };

  const start = async () => {
    setLoading(true);
    try {
      if (kind === "gold") {
        const { data: lk } = await http.post("/gold/lock", { event_id: event.id, grams_mg: parseInt(grams, 10) }, authHeader);
        setLock(lk);
        const g = await initGift({ kind: "gold", grams: parseInt(grams, 10) });
        setGift(g); setPhase("lock");
      } else if (kind === "cash") {
        const g = await initGift({ kind: "cash", amount_kurus: Math.round(parseFloat(amountTL) * 100) });
        setGift(g); setPhase("paying");
      } else if (kind === "donation") {
        const g = await initGift({ kind: "donation", amount_kurus: Math.round(parseFloat(amountTL) * 100), cause_id: causeId });
        setGift(g); setPhase("paying");
      } else if (kind === "coupon") {
        const g = await initGift({ kind: "coupon", coupon_id: couponId });
        setGift(g);
        // no payment step — go straight to confirm
        await confirmDirect(g.gift_id);
      } else if (kind === "message") {
        if (!text.trim()) throw new Error("Mesaj boş olamaz");
        const g = await initGift({ kind: "message", text: text.trim() });
        setGift(g);
        await confirmDirect(g.gift_id);
      }
    } catch (e) { toast.error(e?.response ? fmtErr(e) : e.message); }
    setLoading(false);
  };

  const confirmDirect = async (gid) => {
    const { data } = await http.post("/gifts/confirm", { gift_id: gid, outcome: "captured" }, authHeader);
    if (data.status === "paid") { toast.success("Hediye iletildi"); setPhase("done"); onDone && onDone(); }
    else { toast.error("Başarısız"); setPhase("form"); }
  };

  const confirm = async (outcome = "captured") => {
    setLoading(true);
    try {
      const { data } = await http.post("/gifts/confirm", {
        gift_id: gift.gift_id, price_lock_id: lock?.lock_id, outcome,
      }, authHeader);
      if (data.status === "paid") { toast.success("Hediye iletildi"); setPhase("done"); onDone && onDone(); }
      else { toast.error("Ödeme başarısız. Tekrar deneyin."); setPhase("form"); }
    } catch (e) { toast.error(fmtErr(e)); }
    setLoading(false);
  };

  if (phase === "done") return (
    <div className="p-6 text-center space-y-3" data-testid="gift-done">
      <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 ring-1 ring-primary/40 grid place-items-center pulse-gold">
        <Sparkles className="w-7 h-7 text-primary" />
      </div>
      <div className="font-serif text-2xl">Teşekkürler</div>
      <p className="text-sm text-muted-foreground">Hediyeniz duvara düşüyor.</p>
    </div>
  );

  if (phase === "lock") return (
    <div className="p-6 space-y-6" data-testid="gift-lock">
      <div className="flex items-center gap-4">
        <CountdownRing seconds={seconds} />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{t.lock_countdown}</div>
          <div className="font-serif text-2xl">{fmtGold(lock.grams_mg)}</div>
          <div className="font-mono text-sm text-muted-foreground">{fmtTRY(lock.total_kurus)} · {(lock.kurus_per_mg / 100).toFixed(2)} ₺/mg</div>
        </div>
      </div>
      <div className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/40">{t.demo_notice}</div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => confirm("failed")} disabled={loading} className="rounded-full" data-testid="gift-fail">Demo Fail</Button>
        <Button onClick={() => confirm("captured")} disabled={loading} className="rounded-full" data-testid="gift-confirm">{t.confirm_pay}</Button>
      </div>
    </div>
  );

  if (phase === "paying") return (
    <div className="p-6 space-y-5" data-testid="gift-pay">
      <div className="font-serif text-2xl">{fmtTRY(gift.amount_kurus)}</div>
      {kind === "donation" && <div className="text-xs text-emerald-300 uppercase tracking-wider">{t.donation_to}: {chosenCause?.name}</div>}
      <div className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/40">{t.demo_notice}</div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => confirm("failed")} disabled={loading} className="rounded-full" data-testid="gift-fail">Demo Fail</Button>
        <Button onClick={() => confirm("captured")} disabled={loading} className="rounded-full" data-testid="gift-confirm">{t.confirm_pay}</Button>
      </div>
    </div>
  );

  return (
    <div className="p-5 sm:p-6 space-y-4" data-testid="gift-form">
      <Tabs value={kind} onValueChange={setKind}>
        <TabsList className="w-full grid grid-cols-5 gap-1 h-auto p-1">
          <TabsTrigger value="cash" disabled={!event.accept_cash} className="flex-col gap-1 h-14 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-cash">
            <Banknote className="w-4 h-4" />{t.cash}
          </TabsTrigger>
          <TabsTrigger value="gold" disabled={!event.accept_gold} className="flex-col gap-1 h-14 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-gold">
            <GemIcon className="w-4 h-4" />{t.gold}
          </TabsTrigger>
          <TabsTrigger value="coupon" className="flex-col gap-1 h-14 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-coupon">
            <Ticket className="w-4 h-4" />{t.coupon}
          </TabsTrigger>
          <TabsTrigger value="message" className="flex-col gap-1 h-14 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-message">
            <MessageCircleHeart className="w-4 h-4" />{t.message_gift}
          </TabsTrigger>
          <TabsTrigger value="donation" className="flex-col gap-1 h-14 text-[11px] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-donation">
            <HandHeart className="w-4 h-4" />{t.donation}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cash" className="space-y-3 mt-4">
          <Label>{t.amount}</Label>
          <Input type="number" min={10} step={10} value={amountTL} onChange={(e) => setAmountTL(e.target.value)} data-testid="gift-amount" />
          <div className="flex flex-wrap gap-2">
            {[100, 250, 500, 1000, 2500].map(v => <Button key={v} variant="outline" size="sm" className="rounded-full" onClick={() => setAmountTL(v)}>{v} ₺</Button>)}
          </div>
        </TabsContent>

        <TabsContent value="gold" className="space-y-3 mt-4">
          <Label>{t.grams} · 1 gr = 1000 mg</Label>
          <Input type="number" min={100} step={100} value={grams} onChange={(e) => setGrams(e.target.value)} data-testid="gift-grams" />
          <div className="flex flex-wrap gap-2">
            {[500, 1000, 2500, 5000].map(v => <Button key={v} variant="outline" size="sm" className="rounded-full" onClick={() => setGrams(v)}>{v / 1000} gr</Button>)}
          </div>
        </TabsContent>

        <TabsContent value="coupon" className="space-y-3 mt-4">
          <Label>{t.choose_coupon}</Label>
          <Select value={couponId} onValueChange={setCouponId}>
            <SelectTrigger data-testid="coupon-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {catalog.coupons.map(c => (
                <SelectItem key={c.coupon_id} value={c.coupon_id} data-testid={`coupon-${c.coupon_id}`}>
                  <span className="flex items-center gap-2"><Ticket className="w-3 h-3 text-amber-400" /><span className="font-serif">{c.partner}</span> · {c.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {chosenCoupon && <div className="text-xs text-muted-foreground">{t.coupon_desc} · Değer: <span className="font-mono">{fmtTRY(chosenCoupon.value_kurus)}</span></div>}
        </TabsContent>

        <TabsContent value="message" className="space-y-3 mt-4">
          <Label>{t.only_note}</Label>
          <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} maxLength={280} placeholder="Sizin için özel bir tebrik yazın..." data-testid="message-text" />
          <div className="text-xs text-muted-foreground">{t.message_gift_desc}</div>
        </TabsContent>

        <TabsContent value="donation" className="space-y-3 mt-4">
          <Label>{t.choose_cause}</Label>
          <Select value={causeId} onValueChange={setCauseId}>
            <SelectTrigger data-testid="cause-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {catalog.causes.map(c => (
                <SelectItem key={c.cause_id} value={c.cause_id} data-testid={`cause-${c.cause_id}`}>
                  <span className="flex items-center gap-2"><HandHeart className="w-3 h-3 text-emerald-300" /> {c.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label>{t.amount}</Label>
          <Input type="number" min={10} step={10} value={amountTL} onChange={(e) => setAmountTL(e.target.value)} data-testid="donation-amount" />
          <div className="flex flex-wrap gap-2">
            {[100, 250, 500, 1000].map(v => <Button key={v} variant="outline" size="sm" className="rounded-full" onClick={() => setAmountTL(v)}>{v} ₺</Button>)}
          </div>
        </TabsContent>
      </Tabs>

      {kind !== "message" && <div><Label>{t.message}</Label><Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={140} data-testid="gift-message" /></div>}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40">
        <Label className="text-sm">{t.anonymous}</Label>
        <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="gift-anon" />
      </div>
      <Button onClick={start} disabled={loading} className="w-full rounded-full h-11" data-testid="gift-continue">
        {loading ? "..." : (kind === "message" ? t.send_message : t.continue)}
      </Button>
    </div>
  );
}

export default function GuestEvent() {
  const { key } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const [ev, setEv] = useState(null);
  const [wall, setWall] = useState({ totals: { cash_kurus: 0, gold_mg: 0, count: 0 }, gifts: [] });
  const [catalog, setCatalog] = useState({ coupons: [], causes: [] });
  const [chat, setChat] = useState([]);
  const [text, setText] = useState("");
  const [presence, setPresence] = useState(0);
  const wsRef = useRef(null);
  const chatContainerRef = useRef(null);
  const guestToken = ev ? localStorage.getItem(`guest:${ev.id}`) : null;
  const displayName = ev ? (localStorage.getItem(`guest_name:${ev.id}`) || "Guest") : "";
  const [openGift, setOpenGift] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get(`/public/events/${key}`);
        setEv(data);
        const c = await http.get("/gifts/catalog");
        setCatalog(c.data);
      } catch (e) { toast.error(fmtErr(e)); nav(`/g/${key}`); }
    })();
  }, [key, nav]);

  useEffect(() => {
    if (!ev) return;
    const gt = localStorage.getItem(`guest:${ev.id}`);
    if (!gt) { nav(`/g/${key}`); return; }
    http.get(`/public/events/${key}/wall`).then(r => setWall(r.data)).catch(() => {});
    const ws = new WebSocket(`${WS_BASE}/api/ws/events/${ev.id}?token=${encodeURIComponent(gt)}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      const m = JSON.parse(e.data);
      if (m.type === "chat") setChat((c) => [...c.slice(-100), m]);
      if (m.type === "presence") setPresence(m.count || 0);
      if (m.type === "gift") {
        http.get(`/public/events/${key}/wall`).then(r => setWall(r.data)).catch(() => {});
        const meta = KIND_META[m.kind] || KIND_META.cash;
        const label = t[meta.label_key] || m.kind;
        const suffix = m.kind === "gold" ? fmtGold(m.grams_mg) : m.kind === "message" ? label : fmtTRY(m.amount_kurus);
        toast.success(`${m.display_name}: ${suffix}`);
      }
    };
    return () => { try { ws.close(); } catch { /* noop */ } };
  }, [ev, key, nav, t]);

  const send = (e) => {
    e.preventDefault();
    if (!text.trim() || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ text }));
    setText("");
  };

  useEffect(() => {
    const c = chatContainerRef.current;
    if (c) c.scrollTop = c.scrollHeight;
  }, [chat.length]);

  if (!ev) return <div className="mx-auto max-w-7xl px-4 py-10 text-muted-foreground">Yükleniyor...</div>;

  return (
    <div className="mx-auto max-w-7xl px-3 sm:px-6 py-6 pb-28 lg:pb-10">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-primary/80">{t.types?.[ev.event_type] || ev.event_type}</div>
          <h1 className="font-serif text-3xl sm:text-4xl gold-text">{ev.title}</h1>
          <p className="text-sm text-muted-foreground">{fmtDate(ev.starts_at)}</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-500/10 text-red-400 ring-1 ring-red-500/30">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> {t.live_now}
          </span>
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {presence}</span>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <Card className="overflow-hidden">
            <div className="relative aspect-video bg-secondary"><StreamEmbed url={ev.stream_url || ""} /></div>
          </Card>
          <Card className="p-5">
            <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> {t.wall}</h2>
            {wall.gifts.length === 0 ? <p className="text-sm text-muted-foreground">İlk hediyeyi siz gönderin.</p> : (
              <div className="grid sm:grid-cols-2 gap-3">
                {wall.gifts.slice(0, 10).map((g, i) => <GiftCard key={g.id} g={g} i={i} t={t} />)}
              </div>
            )}
          </Card>
        </div>

        <div className="lg:col-span-4">
          <Card className="p-0 overflow-hidden h-[560px] flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="font-serif text-xl">{t.chat}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" /> {presence}</div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2" ref={chatContainerRef} data-testid="chat-list">
              {chat.map((m, i) => (
                <div key={i} className="text-sm animate-float-up">
                  <span className="font-serif text-primary me-2">{m.display_name}</span>
                  <span className="text-foreground/90">{m.text}</span>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="p-3 border-t flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t.say} maxLength={280} data-testid="chat-input" />
              <Button type="submit" size="icon" className="rounded-full" data-testid="chat-send"><Send className="w-4 h-4" /></Button>
            </form>
          </Card>
        </div>
      </div>

      {/* Single Drawer + two responsive triggers (no duplicate content) */}
      <Drawer open={openGift} onOpenChange={setOpenGift}>
        <DrawerContent>
          <DrawerHeader><DrawerTitle className="font-serif text-2xl">{t.give_gift}</DrawerTitle></DrawerHeader>
          <div className="max-h-[80vh] overflow-y-auto">
            {guestToken && catalog.coupons.length > 0 && (
              <GiftFlow event={ev} guestToken={guestToken} displayName={displayName} catalog={catalog}
                onDone={() => { setTimeout(() => setOpenGift(false), 1400); http.get(`/public/events/${key}/wall`).then(r => setWall(r.data)); }} />
            )}
          </div>
        </DrawerContent>
      </Drawer>

      <div className="fixed bottom-0 inset-x-0 z-40 p-3 lg:hidden">
        <Button className="w-full rounded-full h-12 gap-2 shadow-xl" onClick={() => setOpenGift(true)} data-testid="mobile-give-gift">
          <Coins className="w-4 h-4" /> {t.give_gift}
        </Button>
      </div>
      <div className="hidden lg:block fixed bottom-6 end-6 z-40">
        <Button className="rounded-full h-14 px-6 gap-2 shadow-2xl" onClick={() => setOpenGift(true)} data-testid="desktop-give-gift">
          <Coins className="w-5 h-5" /> {t.give_gift}
        </Button>
      </div>
    </div>
  );
}
